import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useContractTypes } from '../hooks/useQueries'
import { useLocale } from '../contexts/LocaleContext'
import { getLocalizedName } from '../lib/localization'
import { useFormatters } from '../lib/format'
import { Badge } from './ui/badge'

/** The habitability verdict of a lease (the worst of its lots), as a badge. */
export function LeaseVerdictBadge({ verdict, testId }: { verdict?: string | null; testId?: string }) {
    const { t } = useTranslation('leases')
    if (!verdict) return <span className="text-muted-foreground">—</span>
    const variant = verdict === 'compliant' ? 'success' : verdict === 'non_compliant' ? 'destructive' : 'warning'
    return (
        <Badge variant={variant} data-testid={testId} data-verdict={verdict}>
            {t(`verdict.${verdict}`, { defaultValue: verdict })}
        </Badge>
    )
}

/** "1 250,00 € per month": a rent in minor units with its currency and periodicity. */
export function useRentLabel() {
    const { t } = useTranslation('leases')
    const { formatMoney } = useFormatters()
    return (amountMinor?: number | null, currency?: string | null, frequency?: string | null) => {
        if (amountMinor === null || amountMinor === undefined) return '—'
        const amount = formatMoney(amountMinor, currency)
        return frequency ? `${amount} ${t(`frequency.${frequency}`, { defaultValue: frequency })}` : amount
    }
}

/** The regime roots (the contract types directly under LEASE), with their localized labels. */
export function useLeaseRegimes(org: string) {
    const { locale } = useLocale()
    const { data: contractTypes = [] } = useContractTypes(org)
    const regimes = useMemo(() => {
        const root = contractTypes.find((ct) => ct.code === 'LEASE')
        if (!root) return []
        return contractTypes
            .filter((ct) => ct.parent_contract_type_uid === root.uid)
            .map((ct) => ({ code: ct.code, label: getLocalizedName((ct.names ?? ct.name) as Record<string, string>, locale) || ct.code }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [contractTypes, locale])
    const regimeLabel = (code?: string | null) => (code ? regimes.find((r) => r.code === code)?.label ?? code : '—')
    return { regimes, regimeLabel }
}
